import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  DateAndTimePicker,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import { addMonths, addWeeks, format } from 'date-fns';

import { useInvertedLocalize } from '../../../hooks';

import './SodReviewTimePicker.css';

type DurationType = '1week' | '3weeks' | '1month' | '3months' | 'custom' | '';

interface DurationChip {
  key: Exclude<DurationType, ''>;
  label: string;
}

interface SodReviewTimePickerProps {
  periodSelection: number;
  setPeriodSelection: React.Dispatch<React.SetStateAction<number>>;
  durationSelection: DurationType;
  setDurationSelection: React.Dispatch<React.SetStateAction<DurationType>>;
  startDate: Date | null;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  endDate: Date | null;
  setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
}

/**
 * SodReviewTimePicker
 */
function SodReviewTimePicker({
  periodSelection,
  setPeriodSelection,
  durationSelection,
  setDurationSelection,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}: SodReviewTimePickerProps) {
  const l = useInvertedLocalize();

  const rangeMaxDate = new Date(Date.now() + 100 * 24 * 60 * 60 * 1000); // Temporary
  const [calculatedEndDate, setCalculatedEndDate] = useState<Date | null>(null);

  const durationChips: DurationChip[] = [
    { key: '1week', label: l('1 week') },
    { key: '3weeks', label: l('3 weeks') },
    { key: '1month', label: l('1 month') },
    { key: '3months', label: l('3 months') },
  ];

  const addTime = (currentDateAndTime: Date, timeToAdd: DurationType): Date => {
    if (!timeToAdd || timeToAdd === 'custom') {
      return currentDateAndTime;
    }

    switch (timeToAdd) {
      case '1week':
        return addWeeks(currentDateAndTime, 1);
      case '3weeks':
        return addWeeks(currentDateAndTime, 3);
      case '1month':
        return addMonths(currentDateAndTime, 1);
      case '3months':
        return addMonths(currentDateAndTime, 3);
      default:
        return currentDateAndTime;
    }
  };

  useEffect(() => {
    if (
      periodSelection === 2 &&
      startDate &&
      durationSelection !== 'custom' &&
      durationSelection !== ''
    ) {
      const calculated: Date = addTime(startDate, durationSelection);

      setCalculatedEndDate(calculated);
    }
  }, [periodSelection, durationSelection, startDate]);

  const showCalculatedEndDate =
    periodSelection === 2 &&
    calculatedEndDate &&
    durationSelection !== 'custom' &&
    durationSelection !== '';

  return (
    <Box className='SodReviewTimePicker'>
      <Box className='SodReviewTimePicker-period'>
        <ToggleButtonGroup
          selection={periodSelection}
          setSelection={setPeriodSelection}>
          <ToggleButton label={l('Now')} />
          <ToggleButton label={l('Future')} />
        </ToggleButtonGroup>
      </Box>
      <Box
        tag='div'
        className={
          periodSelection === 2
            ? 'SodReviewTimePicker-isFuture'
            : 'SodReviewTimePicker-isNow'
        }>
        <DateAndTimePicker
          kind='dateAndTimeStart'
          label={l('Start Date')}
          startDate={startDate}
          setStartDate={setStartDate}
          maxDate={rangeMaxDate}
          minDate={new Date()}
          dateFormat='MM/dd/yyyy, h:mm aa'
          portalId='root'
        />
      </Box>
      <Typography kind='h4'>{l('Duration')}</Typography>
      <Box className='SodReviewTimePicker-durationChips'>
        {durationChips.map((duration) => (
          <Chip
            key={duration.key}
            kind={
              durationSelection === duration.key ? 'boldFilled' : 'outlined'
            }
            label={duration.label}
            onClick={() => setDurationSelection(duration.key)}
            color='brandSecondary'
          />
        ))}
        <Box tag='span' className='SodReviewTimePicker-durationChipsDivider' />
        <Chip
          key='custom'
          kind={durationSelection === 'custom' ? 'boldFilled' : 'outlined'}
          label={l('Custom')}
          onClick={() => setDurationSelection('custom')}
          color='brandSecondary'
        />
      </Box>
      <Box
        tag='div'
        className={
          durationSelection === 'custom'
            ? 'SodReviewTimePicker-endDate-isCustom'
            : 'SodReviewTimePicker-endDate'
        }>
        <DateAndTimePicker
          kind='dateAndTimeStart'
          label={l('End Date')}
          startDate={endDate}
          setStartDate={setEndDate}
          maxDate={rangeMaxDate}
          minDate={new Date()}
          dateFormat='MM/dd/yyyy, h:mm aa'
          portalId='root'
        />
      </Box>
      {showCalculatedEndDate ? (
        <>
          <Typography kind='h3'>{l('End Date')}</Typography>
          <Typography kind='body2' color='brandSecondary'>
            {format(calculatedEndDate, 'MM/dd/yyyy, h:mm aa')}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

export default SodReviewTimePicker;
